import type { MangoGroup, PerpMarketConfig, SpotMarketConfig } from "@blockworks-foundation/mango-client";
import { I80F48, MangoClient, PerpAccount, QUOTE_INDEX } from "@blockworks-foundation/mango-client";
import { InvestinClient } from "@investin/client-sdk";
import { useConnection } from "@solana/wallet-adapter-react";
import type { Connection } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import { BN } from "bn.js";
import { useEffect, useState } from "react";

import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import { useRefreshContext } from "../../contexts/refresh";
import { DEFAULT_MANGO_GROUP_CONFIG, defaultMangoGroupIds } from "../../models/mango";
import type {
  BaseMarketConfig,
  CurrentReturns,
  FundName,
  FundPosition,
  ManagedFund,
  ManagedFundInvestment,
} from "../../types/managedFunds";

const INVESTIN_FUND_DETAILS_URL = "https://sol.beta.investin.pro/fund-details/";

const investinFundInfoURL = (fundAddress: string) =>
  `https://capitalfund-api-1-8ftn8.ondigitalocean.app/fund/${fundAddress}/decentralised`;

type InvestinInvestmentInfo = {
  fund: {
    name: string;
  };
  [key: string]: any;
};

type BaseInvestmentInfo = {
  depositPending: boolean;
  depositAmount: number;
  fundAddress: string;
  currentReturns: CurrentReturns;
  fundName: FundName;
};

type MappedInvestments = {
  [key: string]: BaseInvestmentInfo;
};

const fetchInvestments = async (publicKey: PublicKey, connection: Connection) => {
  try {
    const investinClient = new InvestinClient(connection);
    const data = await investinClient.getInvestmentsByInvestorAddress(publicKey);
    let sumReturns = 0;
    const investments: BaseInvestmentInfo[] = await Promise.all(
      data.map(async (investment) => {
        const { currentReturns, fundAddress, amountInRouter, amount } = investment;
        let { fundName } = investment;
        let investmentInfo: InvestinInvestmentInfo;
        try {
          const response = await fetch(investinFundInfoURL(fundAddress));
          investmentInfo = await response.json();
          fundName = investmentInfo.fund.name;
        } catch (e) {
          errorMessage(e);
        }
        const composition = parseTokenPositions(investment.tokens);
        const fundPK = new PublicKey(fundAddress);
        const mangoPositions = await fetchMangoLendPerps(fundPK, connection);
        const parsedCurrentReturns = parseFloat(currentReturns.toString());
        sumReturns += parsedCurrentReturns;
        const depositAmount = parseFloat(amount.toString());
        const depositPending = parseFloat(amountInRouter.toString()) > 0;
        return {
          depositPending,
          depositAmount,
          fundAddress: investment.fundAddress,
          currentReturns: {
            amount: parsedCurrentReturns,
            composition: [...composition, ...mangoPositions].sort((a, b) => b.amount - a.amount),
          },
          fundName: {
            name: fundName,
            href: INVESTIN_FUND_DETAILS_URL + fundAddress,
          },
        };
      }),
    );
    const mappedInvestments: MappedInvestments = {};
    investments.forEach((investment) => {
      const { fundAddress } = investment;
      const mappedInvestment = mappedInvestments[fundAddress];

      if (mappedInvestment) {
        mappedInvestment.currentReturns.amount += investment.currentReturns.amount;
        mappedInvestment.depositAmount += investment.depositAmount;
      } else {
        mappedInvestments[fundAddress] = investment;
      }
    });

    const finalizedInvestments: ManagedFundInvestment[] = Object.values(mappedInvestments).map(
      (investment, i): ManagedFundInvestment => {
        const { depositPending, depositAmount, currentReturns, fundName, fundAddress } = investment;
        const returnOnInvestment = (currentReturns.amount - depositAmount) / depositAmount;
        return {
          key: `investin-${i}`,
          platform: "Investin",
          returnOnInvestment: depositPending ? "Pending Deposit" : returnOnInvestment,
          currentReturns,
          fundName,
          fundAddress,
        };
      },
    );
    return { sumReturns, investments: finalizedInvestments };
  } catch (e) {
    errorMessage(e);
  }
};

const parseTokenPositions = (tokens: FundPosition[]): FundPosition[] => {
  return tokens.map((token) => {
    const { symbol, mint } = token;
    let { decimals } = token;
    decimals = decimals instanceof BN ? decimals.toNumber() : decimals;
    const amount = token.balance! / 10 ** decimals;
    return {
      symbol,
      amount,
      mint,
      decimals,
    };
  });
};

const parseMangoPositions = (
  accounts: I80F48[] | PerpAccount[],
  markets: SpotMarketConfig[] | PerpMarketConfig[] | BaseMarketConfig[],
  mangoGroup: MangoGroup,
  tag: string,
): FundPosition[] => {
  const positions: FundPosition[] = [];
  accounts.forEach((account: I80F48 | PerpAccount, i: number) => {
    let amount = 0;
    if (account instanceof I80F48) {
      amount = account.toNumber();
    } else if (account instanceof PerpAccount) {
      amount = account.basePosition.toNumber();
    }
    if (amount > 0 && markets) {
      const market = markets[i];
      if (!market) {
        return;
      }
      const token = mangoGroup.tokens[market.marketIndex];
      amount = parseFloat(amount.toFixed(token.decimals));
      positions.push({
        mint: token.mint,
        amount,
        decimals: token.decimals,
        symbol: `${market.baseSymbol}-${tag}`,
      });
    }
  });
  return positions;
};

const fetchMangoLendPerps = async (publicKey: PublicKey, connection: Connection) => {
  const programId = new PublicKey(defaultMangoGroupIds.mangoProgramId);
  const mangoGroupPk = new PublicKey(defaultMangoGroupIds.publicKey);
  const client = new MangoClient(connection, programId);
  const mangoGroup = await client.getMangoGroup(mangoGroupPk);
  const mangoAccounts = await client.getMangoAccountsForOwner(mangoGroup, publicKey, true);
  const mangoCache = await mangoGroup.loadCache(connection);
  await mangoGroup.loadRootBanks(connection);
  const lendPositions: FundPosition[] = [];
  const perpPositions: FundPosition[] = [];
  for (const mangoAccount of mangoAccounts) {
    const defaultMangoGroupConfig = DEFAULT_MANGO_GROUP_CONFIG!;
    const spotMarkets: (SpotMarketConfig | { baseSymbol: string; marketIndex: number })[] = [
      ...defaultMangoGroupConfig.spotMarkets,
    ];
    const perpMarkets = defaultMangoGroupConfig.perpMarkets;
    const quoteDeposit = mangoAccount.getUiDeposit(mangoCache.rootBankCache[QUOTE_INDEX], mangoGroup, QUOTE_INDEX);
    const mangoDeposits = [quoteDeposit, ...mangoAccount.deposits];
    const quoteSymbol = defaultMangoGroupConfig.quoteSymbol;
    spotMarkets.unshift({ baseSymbol: quoteSymbol, marketIndex: QUOTE_INDEX });
    Array.prototype.push.apply(lendPositions, parseMangoPositions(mangoDeposits, spotMarkets, mangoGroup, "LEND"));
    Array.prototype.push.apply(
      perpPositions,
      parseMangoPositions(mangoAccount.perpAccounts, perpMarkets, mangoGroup, "PERP"),
    );
  }
  return [...lendPositions, ...perpPositions];
};

const errorMessage = (e: any) => {
  console.error("INVESTIN ERROR:", e);
};

export const useInvestin = () => {
  const [investments, setInvestments] = useState<ManagedFund[]>([]);
  const { connection } = useConnection();
  const { connected, publicKey } = useEnrichedWallet();
  const { refreshID } = useRefreshContext();

  useEffect(() => setInvestments([]), [connected]);

  useEffect(() => {
    const fetchUserInvestments = async () => {
      if (publicKey) {
        return await fetchInvestments(publicKey, connection);
      }
    };
    void fetchUserInvestments().then((result) => {
      if (result) {
        setInvestments([result]);
      }
    });
  }, [publicKey, connection, refreshID]);

  return investments;
};
